// Data hook for the business-case overlay.
//
// Server state (costs, savings, driver + category catalogues, tenant currency)
// is fetched here — never in component bodies (frontend-constraints rule). The
// hook exposes loading/data/error, a `reload` refetch, mutate helpers that wrap
// the US4 client, and the `hasCost` / `hasSavings` booleans the completion
// layer (US7) consumes. UI-local state (open panel, search, collapsed sections)
// stays in the components via `useState`.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { portalApi, ApiError } from '../../api/client';
import type {
  PortalCostCreateRequest,
  PortalCostDriver,
  PortalCostItem,
  PortalCostUpdateRequest,
  PortalSavingsCategory,
  PortalSavingsCreateRequest,
  PortalSavingsItem,
  PortalSavingsUpdateRequest,
} from '../../api/types';
import { makeCurrencyFormatter } from '../../lib/formatCurrency';

export interface BusinessCaseData {
  costs: PortalCostItem[];
  savings: PortalSavingsItem[];
  drivers: PortalCostDriver[];
  categories: PortalSavingsCategory[];
  currency: string;
}

export interface UseBusinessCaseResult {
  loading: boolean;
  error: string | null;
  data: BusinessCaseData | null;
  /** True once at least one cost / savings line item exists. */
  hasCost: boolean;
  hasSavings: boolean;
  /** Currency formatter bound to the tenant currency. */
  formatCurrency: (value: number | null | undefined) => string;
  reload: () => Promise<void>;
  createCost: (body: PortalCostCreateRequest) => Promise<void>;
  updateCost: (costId: string, body: PortalCostUpdateRequest) => Promise<void>;
  deleteCost: (costId: string) => Promise<void>;
  createSavings: (body: PortalSavingsCreateRequest) => Promise<void>;
  updateSavings: (
    savingsId: string,
    body: PortalSavingsUpdateRequest,
  ) => Promise<void>;
  deleteSavings: (savingsId: string) => Promise<void>;
}

/**
 * Load and mutate the business case for one idea.
 *
 * @param ideaId The saved idea id, or null when the idea is unsaved (no fetch).
 */
export function useBusinessCase(ideaId: string | null): UseBusinessCaseResult {
  const [loading, setLoading] = useState<boolean>(ideaId != null);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<BusinessCaseData | null>(null);

  const load = useCallback(async () => {
    if (!ideaId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [costsRes, savingsRes, driversRes, categoriesRes, tenantRes] =
        await Promise.all([
          portalApi.listCosts(ideaId),
          portalApi.listSavings(ideaId),
          portalApi.listCostDrivers(),
          portalApi.listSavingsCategories(),
          portalApi.getTenant(),
        ]);
      setData({
        costs: [...costsRes.one_time, ...costsRes.recurring],
        savings: [...savingsRes.one_time, ...savingsRes.recurring],
        drivers: driversRes.cost_drivers,
        categories: categoriesRes.categories,
        currency: tenantRes.currency,
      });
    } catch (err) {
      // A 401 fires the global interceptor; do not surface it as a local error.
      if (!(err instanceof ApiError && err.status === 401)) {
        setError('Could not load the business case. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [ideaId]);

  useEffect(() => {
    void load();
  }, [load]);

  const createCost = useCallback(
    async (body: PortalCostCreateRequest) => {
      if (!ideaId) return;
      await portalApi.createCost(ideaId, body);
      await load();
    },
    [ideaId, load],
  );
  const updateCost = useCallback(
    async (costId: string, body: PortalCostUpdateRequest) => {
      if (!ideaId) return;
      await portalApi.updateCost(ideaId, costId, body);
      await load();
    },
    [ideaId, load],
  );
  const deleteCost = useCallback(
    async (costId: string) => {
      if (!ideaId) return;
      await portalApi.deleteCost(ideaId, costId);
      await load();
    },
    [ideaId, load],
  );
  const createSavings = useCallback(
    async (body: PortalSavingsCreateRequest) => {
      if (!ideaId) return;
      await portalApi.createSavings(ideaId, body);
      await load();
    },
    [ideaId, load],
  );
  const updateSavings = useCallback(
    async (savingsId: string, body: PortalSavingsUpdateRequest) => {
      if (!ideaId) return;
      await portalApi.updateSavings(ideaId, savingsId, body);
      await load();
    },
    [ideaId, load],
  );
  const deleteSavings = useCallback(
    async (savingsId: string) => {
      if (!ideaId) return;
      await portalApi.deleteSavings(ideaId, savingsId);
      await load();
    },
    [ideaId, load],
  );

  const formatCurrency = useMemo(
    () => makeCurrencyFormatter(data?.currency ?? 'USD'),
    [data?.currency],
  );

  return {
    loading,
    error,
    data,
    hasCost: (data?.costs.length ?? 0) > 0,
    hasSavings: (data?.savings.length ?? 0) > 0,
    formatCurrency,
    reload: load,
    createCost,
    updateCost,
    deleteCost,
    createSavings,
    updateSavings,
    deleteSavings,
  };
}
